/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { RECORD_STAGES } from '@/lib/stages';

/**
 * Where a person is, as the left rail says it.
 *
 * One function, for every route, so the rail and the breadcrumb on the page
 * cannot disagree. A URL is not always the answer: starting an agreement
 * lives under `/workflows/…/launch`, and lit Workflows while the person was
 * doing agreement work; a finished agreement's page lives under
 * `/agreements/…` and is one of the Records. The section is what the person
 * is doing, read from the route and what the page knows about itself.
 */
export type RailSection =
  | 'dashboard'
  | 'agreements'
  | 'records'
  | 'packets'
  | 'workflows'
  | 'deadlines'
  | 'counterparties'
  | 'import'
  | 'settings'
  | 'help';

export interface RailContext {
  /** The stage of the agreement the page shows, once the page knows it. */
  agreementStage?: string | undefined;
  /** Launching a packet's step: the packet's id from the launch link. */
  packet?: string | undefined;
}

/** Whether an agreement at this stage is finished: one of the Records. */
export function isRecordStage(stage: string | undefined): boolean {
  return stage !== undefined && (RECORD_STAGES as readonly string[]).includes(stage);
}

/** The section an agreement's page belongs to: Records once it is finished. */
export function agreementSection(stage: string | undefined): 'agreements' | 'records' {
  return isRecordStage(stage) ? 'records' : 'agreements';
}

/** The section a path belongs to, or null where the rail lights nothing. */
export function railSection(path: string, context: RailContext = {}): RailSection | null {
  const parts = path.split('/').filter(Boolean);
  const [first, second, third] = parts;

  switch (first) {
    case undefined:
    case 'dashboard':
      return 'dashboard';
    case 'agreements':
      // The list is the work; one agreement's page is wherever it now is.
      return second === undefined
        ? 'agreements'
        : agreementSection(context.agreementStage);
    case 'records':
      return 'records';
    case 'workflows':
      // Starting an agreement is agreement work, whatever URL it sits under --
      // a packet's step included: started packets are followed on Agreements.
      if (second !== undefined && third === 'launch') {
        return 'agreements';
      }
      return 'workflows';
    case 'packets':
      // The list is setting packets up, under Admin; one started packet's
      // page is the work, followed on Agreements' Packets tab.
      return second === undefined ? 'packets' : 'agreements';
    case 'deadlines':
      return 'deadlines';
    case 'counterparties':
      return 'counterparties';
    case 'import':
      return 'import';
    case 'settings':
      return 'settings';
    case 'help':
      return 'help';
    default:
      return null;
  }
}

/** The way back from a started packet: Agreements, narrowed to packets. */
export const PACKETS_TAB = {
  label: 'Agreements',
  to: '/agreements',
  search: { kind: 'packet' },
};

/** How each section is named, and where its list is: the breadcrumb's first step. */
export const SECTION_HOME: Record<RailSection, { label: string; to: string }> = {
  dashboard: { label: 'Dashboard', to: '/dashboard' },
  agreements: { label: 'Agreements', to: '/agreements' },
  // Completed agreements: the Completed tab, which `/records` opens.
  records: { label: 'Agreements', to: '/records' },
  // Packet templates are the second tab of Templates; `/packets` opens it.
  packets: { label: 'Templates', to: '/packets' },
  workflows: { label: 'Templates', to: '/workflows' },
  deadlines: { label: 'Deadlines', to: '/deadlines' },
  counterparties: { label: 'Counterparties', to: '/counterparties' },
  import: { label: 'Import', to: '/import' },
  settings: { label: 'Settings', to: '/settings' },
  help: { label: 'Help', to: '/help' },
};
